import React, { useState, useEffect } from 'react';
import theme from '../../../theme/globalTheme';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
  Card, Result, Button, Alert, Typography, Space, Descriptions, 
  Timeline, Tag, Divider, Image, Spin
} from 'antd';
import { 
  CheckCircleOutlined, 
  MailOutlined, 
  DownloadOutlined,
  PrinterOutlined,
  ArrowLeftOutlined,
  CalendarOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import { supabase } from '../../../api/supabase';
import { generateContractPDFBase64 } from './ContractPdfTemplate';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const AdminConfirmationPage = () => {
  const { reservationId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [error, setError] = useState(null);

  // Company theme colors
  const theme = {
    primary: '#000000',
    secondary: '#ffffff',
    accent: '#666666',
    lightGrey: '#f5f5f5',
    mediumGrey: '#d9d9d9',
    success: '#52c41a',
    warning: '#faad14',
    error: '#ff4d4f',
    gradient: 'linear-gradient(135deg, #000000 0%, #434343 100%)'
  };

  useEffect(() => {
    console.log('✅ Admin confirmation page loaded');
    console.log('Reservation ID:', reservationId);
    
    if (reservationId) {
      fetchReservationAndSendConfirmation();
    }
  }, [reservationId]);

  const fetchReservationAndSendConfirmation = async () => {
    setLoading(true);
    try {
      // Récupérer la réservation complète
      const { data, error: fetchError } = await supabase
        .from('lunch_reservations')
        .select(`
          *,
          lunch_reservation_items (*),
          lunch_signed_contracts (*),
          lunch_payments (*)
        `)
        .eq('id', reservationId)
        .single();

      if (fetchError) {
        throw new Error('Réservation non trouvée');
      }

      setReservation(data);
      console.log('📋 Reservation data loaded:', data);

      // Vérifier si déjà confirmée
      if (data.status === 'confirmed') {
        setConfirmationSent(true);
        console.log('✅ Reservation already confirmed');
      } else {
        // Envoyer la confirmation
        await processConfirmation(data);
      }

    } catch (error) {
      console.error('❌ Error processing confirmation:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const processConfirmation = async (reservationData) => {
    try {
      console.log('📧 Processing admin confirmation...');

      // 1. Mettre à jour le statut en base
      const { error: updateError } = await supabase
        .from('lunch_reservations')
        .update({ 
          status: 'confirmed',
          confirmed_at: new Date().toISOString(),
          confirmed_by: 'admin'
        })
        .eq('id', reservationData.id);

      if (updateError) throw updateError;

      // 2. Générer le PDF du contrat
      const contractPdfResult = await generateContractPDFBase64(reservationData);
      
      // 3. Envoyer l'email de confirmation au client
      await sendClientConfirmationEmail(reservationData, contractPdfResult.base64);

      // 4. Enregistrer l'envoi de confirmation
      const { error: logError } = await supabase
        .from('lunch_email_logs')
        .insert([{
          lunch_reservation_id: reservationData.id,
          email_type: 'client_confirmation',
          recipient: reservationData.client_email,
          sent_at: new Date().toISOString(),
          sent_by: 'admin',
          status: 'sent'
        }]);

      if (logError) {
        console.warn('⚠️ Warning logging email:', logError);
      }

      setConfirmationSent(true);
      console.log('✅ Confirmation process completed');

    } catch (error) {
      console.error('❌ Error in confirmation process:', error);
      throw error;
    }
  };

  const sendClientConfirmationEmail = async (reservationData, contractPdfBase64) => {
    try {
      console.log('📧 Sending confirmation email to client...');
      
      const emailData = {
        type: 'client_confirmation',
        to: reservationData.client_email,
        to_name: reservationData.client_name,
        subject: `✅ Confirmation Lunch Master Ski Pilou - ${dayjs(reservationData.charter_date).format('DD/MM/YYYY')}`,
        message: generateClientConfirmationMessage(reservationData),
        reservation: {
          id: reservationData.id,
          client_name: reservationData.client_name,
          client_email: reservationData.client_email,
          client_phone: reservationData.client_phone,
          charter_date: reservationData.charter_date,
          boat_name: reservationData.boat_name,
          trip_type: reservationData.trip_type,
          total_amount: reservationData.total_amount
        },
        items: reservationData.lunch_reservation_items || [],
        attachments: contractPdfBase64 ? [{
          filename: `Contrat_MSP_${reservationData.client_name.replace(/\s+/g, '_')}_${dayjs(reservationData.charter_date).format('YYYYMMDD')}.pdf`,
          content: contractPdfBase64,
          type: 'application/pdf'
        }] : [],
        timestamp: new Date().toISOString()
      };

      // Envoyer via Make (Integromat)
      const webhookUrl = process.env.REACT_APP_MAKE_CONFIRMATION_WEBHOOK;
      if (webhookUrl) {
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailData)
        });

        if (response.ok) {
          console.log('✅ Client confirmation email sent via Make');
        } else {
          throw new Error(`Make webhook failed: ${response.status}`);
        }
      } else {
        console.warn('⚠️ Make confirmation webhook not configured');
      }

    } catch (error) {
      console.error('❌ Error sending client confirmation:', error);
      throw error;
    }
  };

  const generateClientConfirmationMessage = (reservationData) => {
    const itemsList = reservationData.lunch_reservation_items
      ?.map(item => `• ${item.item_name}: ${item.quantity} x ${item.unit_price}€`)
      .join('\n') || 'Aucun article';

    return `
Bonjour ${reservationData.client_name},

🎉 Excellente nouvelle ! Votre réservation lunch Master Ski Pilou est officiellement confirmée !

📅 DÉTAILS DE VOTRE SORTIE CONFIRMÉE:
• Date: ${dayjs(reservationData.charter_date).format('dddd DD/MM/YYYY')}
• Bateau: ${reservationData.boat_name}
• Type de sortie: ${reservationData.trip_type}

🍽️ VOTRE MENU CONFIRMÉ:
${itemsList}

💰 Montant total réglé: ${reservationData.total_amount}€
✅ Paiement confirmé
✅ Contrat signé électroniquement

📄 Vous trouverez votre contrat signé en pièce jointe de cet email.

📞 INFORMATIONS IMPORTANTES:
• Rendez-vous: Port de Gustavia (détails précis communiqués 24h avant)
• Heure: Nous vous contacterons pour confirmer l'heure exacte
• Contact d'urgence le jour J: 05 90 27 91 79

🏝️ PRÉPAREZ-VOUS POUR UNE JOURNÉE INOUBLIABLE !
L'équipe Master Ski Pilou a hâte de vous accueillir à bord pour cette magnifique journée à Saint-Barthélemy.

N'hésitez pas à nous contacter si vous avez des questions.

Merci de votre confiance !

L'équipe Master Ski Pilou
Rue Jeanne d'Arc, GUSTAVIA - 97133 Saint-Barthélemy
Tél: 05 90 27 91 79
    `.trim();
  };

  const downloadConfirmationReceipt = async () => {
    try {
      if (!reservation) return;
      
      const contractPdfResult = await generateContractPDFBase64(reservation);
      if (contractPdfResult.success) {
        const link = document.createElement('a');
        link.href = contractPdfResult.base64;
        link.download = `Confirmation_MSP_${reservation.client_name.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD')}.pdf`;
        link.click();
      }
    } catch (error) {
      console.error('❌ Error downloading confirmation:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: theme.gradient
      }}>
        <Card style={{ textAlign: 'center', maxWidth: 400, border: `2px solid ${theme.mediumGrey}` }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Envoi de la confirmation en cours...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: theme.gradient
      }}>
        <Card style={{ maxWidth: 500, border: `2px solid ${theme.error}` }}>
          <Result
            status="error"
            title="Erreur de Confirmation"
            subTitle={error}
            extra={
              <Button 
                type="primary" 
                icon={<ArrowLeftOutlined />}
                onClick={() => window.history.back()}
                style={{ 
                  backgroundColor: theme.primary, 
                  borderColor: theme.primary 
                }}
              >
                Retour au CRM
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: theme.gradient,
      padding: '24px'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <Card style={{ 
          marginBottom: '24px', 
          textAlign: 'center',
          border: `2px solid ${theme.mediumGrey}`,
          background: theme.secondary
        }}>
          <Title level={2} style={{ margin: '0 0 8px 0', color: theme.primary }}>
            🏝️ Master Ski Pilou - CRM
          </Title>
          <Title level={3} style={{ margin: '0 0 16px 0', color: theme.success }}>
            Confirmation Envoyée
          </Title>
        </Card>

        {/* Résultat principal */}
        <Card style={{ 
          border: `2px solid ${theme.success}`,
          background: theme.secondary,
          marginBottom: '24px'
        }}>
          <Result
            status="success"
            title={<span style={{ color: theme.success }}>Email de Confirmation Envoyé ! ✅</span>}
            subTitle={
              <div>
                <Paragraph style={{ fontSize: '16px', color: theme.accent }}>
                  La confirmation a été envoyée avec succès à <strong style={{ color: theme.primary }}>{reservation?.client_email}</strong>
                </Paragraph>
                
                <Alert
                  message="📧 Email envoyé avec succès"
                  description={
                    <div style={{ textAlign: 'left' }}>
                      <p><strong>Contenu de l'email :</strong></p>
                      <ul>
                        <li>✅ Confirmation officielle de la réservation</li>
                        <li>📄 Contrat signé en pièce jointe (PDF)</li>
                        <li>📅 Détails complets de la sortie</li>
                        <li>🛒 Récapitulatif des articles commandés</li>
                        <li>📞 Informations de contact et rendez-vous</li>
                      </ul>
                    </div>
                  }
                  type="success"
                  showIcon
                  style={{ margin: '24px 0', textAlign: 'left' }}
                />
              </div>
            }
            extra={[
              <Space key="actions" direction="vertical" style={{ width: '100%' }}>
                <Space size="large">
                  <Button 
                    type="primary" 
                    icon={<ArrowLeftOutlined />}
                    onClick={() => window.location.href = '/admin/lunch-reservations'}
                    style={{ 
                      backgroundColor: theme.primary, 
                      borderColor: theme.primary 
                    }}
                  >
                    Retour au CRM
                  </Button>
                  
                  <Button 
                    icon={<DownloadOutlined />}
                    onClick={downloadConfirmationReceipt}
                  >
                    Télécharger PDF
                  </Button>
                </Space>
              </Space>
            ]}
          />
        </Card>

        {/* Détails de la réservation confirmée */}
        {reservation && (
          <Card style={{ 
            border: `1px solid ${theme.mediumGrey}`,
            background: theme.secondary,
            marginBottom: '24px'
          }}>
            <Title level={4} style={{ color: theme.primary }}>
              📋 Détails de la Réservation Confirmée
            </Title>
            
            <Descriptions 
              column={2} 
              bordered 
              size="small"
              style={{ marginBottom: '24px' }}
            >
              <Descriptions.Item label="Client" span={2}>
                <div>
                  <Text strong style={{ fontSize: '16px' }}>{reservation.client_name}</Text>
                  <div><Text type="secondary">{reservation.client_email}</Text></div>
                  {reservation.client_phone && (
                    <div><Text type="secondary">📞 {reservation.client_phone}</Text></div>
                  )}
                </div>
              </Descriptions.Item>
              
              <Descriptions.Item label="Date Charter">
                <div>
                  <CalendarOutlined style={{ marginRight: '8px', color: theme.primary }} />
                  <Text strong>{dayjs(reservation.charter_date).format('dddd DD/MM/YYYY')}</Text>
                </div>
              </Descriptions.Item>
              
              <Descriptions.Item label="Statut">
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  CONFIRMÉ ✅
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="Bateau">
                {reservation.boat_name}
              </Descriptions.Item>
              
              <Descriptions.Item label="Type de sortie">
                {reservation.trip_type}
              </Descriptions.Item>
              
              <Descriptions.Item label="Montant Total">
                <Text strong style={{ fontSize: '16px', color: theme.success }}>
                  {reservation.total_amount}€
                </Text>
              </Descriptions.Item>
              
              <Descriptions.Item label="Date de confirmation">
                <Text strong style={{ color: theme.success }}>
                  {dayjs(reservation.confirmed_at || new Date()).format('DD/MM/YYYY HH:mm')}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            {/* Timeline du processus */}
            <Divider>Historique de la Réservation</Divider>
            
            <Timeline
              items={[
                {
                  color: theme.colors.white,
                  children: (
                    <div>
                      <Text strong>Réservation créée</Text>
                      <div><Text type="secondary">{dayjs(reservation.created_at).format('DD/MM/YYYY HH:mm')}</Text></div>
                    </div>
                  )
                },
                {
                  color: theme.colors.white,
                  children: (
                    <div>
                      <Text strong>Menu sélectionné</Text>
                      <div><Text type="secondary">{reservation.lunch_reservation_items?.length || 0} articles</Text></div>
                    </div>
                  )
                },
                ...(reservation.lunch_signed_contracts?.length > 0 ? [{
                  color: theme.colors.white,
                  children: (
                    <div>
                      <Text strong>Contrat signé électroniquement</Text>
                      <div><Text type="secondary">{dayjs(reservation.lunch_signed_contracts[0].signature_date).format('DD/MM/YYYY HH:mm')}</Text></div>
                    </div>
                  )
                }] : []),
                ...(reservation.payment_date ? [{
                  color: theme.colors.white,
                  children: (
                    <div>
                      <Text strong>Paiement effectué</Text>
                      <div><Text type="secondary">{dayjs(reservation.payment_date).format('DD/MM/YYYY HH:mm')} - {reservation.total_amount}€</Text></div>
                    </div>
                  )
                }] : []),
                {
                  color: theme.colors.white,
                  children: (
                    <div>
                      <Text strong>✅ Confirmation envoyée au client</Text>
                      <div><Text type="secondary" style={{ color: theme.success }}>
                        {dayjs().format('DD/MM/YYYY HH:mm')} - Email + PDF
                      </Text></div>
                    </div>
                  )
                }
              ]}
            />
          </Card>
        )}

        {/* Articles commandés */}
        {reservation?.lunch_reservation_items?.length > 0 && (
          <Card style={{ 
            border: `1px solid ${theme.mediumGrey}`,
            background: theme.secondary
          }}>
            <Title level={4} style={{ color: theme.primary }}>🛒 Articles Confirmés</Title>
            
            <div style={{ marginBottom: '16px' }}>
              {reservation.lunch_reservation_items.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: index < reservation.lunch_reservation_items.length - 1 ? `1px solid ${theme.lightGrey}` : 'none'
                }}>
                  <div>
                    <Text strong style={{ color: theme.primary, fontSize: '14px' }}>{item.item_name}</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Quantité: {item.quantity} x {item.unit_price}€
                      </Text>
                    </div>
                  </div>
                  <Text strong style={{ color: theme.primary, fontSize: '14px' }}>
                    {item.total_price}€
                  </Text>
                </div>
              ))}
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0 8px 0',
                borderTop: `2px solid ${theme.success}`,
                marginTop: '16px'
              }}>
                <Text strong style={{ fontSize: '16px', color: theme.primary }}>TOTAL CONFIRMÉ:</Text>
                <Text strong style={{ fontSize: '18px', color: theme.success }}>
                  {reservation.total_amount}€
                </Text>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminConfirmationPage;